import { useEffect, useRef, useState } from 'react';

/**
 * Parse JSON safely with fallback
 */
function safeJsonParse<T>(str: string, fallback: T): T {
  try {
    return JSON.parse(str) as T;
  } catch {
    return fallback;
  }
}

/**
 * Normalize stand data - convert JSON strings to objects
 */
function normalizeStand(s: any): any {
  if (!s) return s;
  return {
    ...s,
    svg: typeof s.svg === 'string' && s.svg ? safeJsonParse(s.svg, null) : s.svg,
    options: typeof s.options === 'string' && s.options ? safeJsonParse(s.options, []) : s.options,
  };
}

interface Stand {
  id: string;
  status: string;
  documentsSubmittedAt?: string;
  reservedAt?: string;
  [key: string]: any;
}

interface EventWithStands {
  id: string;
  [key: string]: any;
}

interface UseEventsPollingOptions {
  enabled?: boolean;
  interval?: number; // in milliseconds, default 20000 (20s)
  standStatusFilter?: string[]; // Default: ['pending', 'document_review', 'payment_verification']
  onEventsChange?: (events: EventWithStands[]) => void;
  onStandsChange?: (stands: (Stand & { eventId: string })[]) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook specializzato per polling di eventi con nested stands
 * Ottimizzato per admin dashboard con cache invalidation
 *
 * @param options - Opzioni di configurazione
 * @returns Object con events, pendingStands, e loading state
 */
export function useEventsPolling(options: UseEventsPollingOptions = {}) {
  const {
    enabled = true,
    interval = 20000, // 20 seconds default come suggerito da analyzer-events
    standStatusFilter = ['pending', 'document_review', 'payment_verification'],
    onEventsChange,
    onStandsChange,
    onError
  } = options;

  const [events, setEvents] = useState<EventWithStands[]>([]);
  const [pendingStands, setPendingStands] = useState<(Stand & { eventId: string })[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  // Cache refs
  const eventsCacheRef = useRef<Map<string, EventWithStands>>(new Map());
  const standsCacheRef = useRef<Map<string, (Stand & { eventId: string })[]>>(new Map());
  const eventsHashRef = useRef<string>('');

  /**
   * Calcola hash degli eventi per cache invalidation
   */
  const calculateEventsHash = (eventsList: EventWithStands[]): string => {
    return eventsList.map(e => e.id).sort().join(',');
  };

  /**
   * Calcola hash degli stand per cache invalidation
   */
  const calculateStandsHash = (standsList: (Stand & { eventId: string })[]): string => {
    return standsList.map(s => `${s.eventId}-${s.id}-${s.status}`).sort().join(',');
  };

  /**
   * Fetch events from API
   */
  const fetchEvents = async (): Promise<EventWithStands[]> => {
    const response = await fetch('/api/events');
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  };

  /**
   * Fetch stands for a specific event
   */
  const fetchStandsForEvent = async (eventId: string): Promise<Stand[]> => {
    const response = await fetch(`/api/events/${eventId}/stands`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  };

  /**
   * Polling principale con cache invalidation
   */
  useEffect(() => {
    if (!enabled) {
      return;
    }

    let isMounted = true;
    let timer: NodeJS.Timeout | null = null;
    let isPolling = false;

    const poll = async () => {
      // Previene polling concorrenti
      if (isPolling || !isMounted) {
        return;
      }

      isPolling = true;
      setIsLoading(true);

      try {
        // 1. Fetch events
        const eventsList = await fetchEvents();
        if (!isMounted) return;

        // Cache invalidation check per events
        const newEventsHash = calculateEventsHash(eventsList);
        const eventsChanged = newEventsHash !== eventsHashRef.current;

        if (eventsChanged) {
          eventsHashRef.current = newEventsHash;
          setEvents(eventsList);

          // Update cache
          eventsCacheRef.current.clear();
          eventsList.forEach(event => {
            eventsCacheRef.current.set(event.id, event);
          });

          if (onEventsChange) {
            onEventsChange(eventsList);
          }
        }

        // 2. Fetch stands per ogni evento (solo se events sono cambiati o prima volta)
        if (eventsChanged || standsCacheRef.current.size === 0) {
          const standsByEvent: { [eventId: string]: (Stand & { eventId: string })[] } = {};

          // Cleanup cache per eventi rimossi
          const currentEventIds = new Set(eventsList.map(e => e.id));
          for (const eventId of standsCacheRef.current.keys()) {
            if (!currentEventIds.has(eventId)) {
              standsCacheRef.current.delete(eventId);
            }
          }

          // Fetch stands in parallelo per performance
          const standsPromises = eventsList.map(async (event) => {
            try {
              const stands = await fetchStandsForEvent(event.id);
              if (!isMounted) return [];

              const relevantStands = stands
                .filter(s => standStatusFilter.includes(s.status))
                .map(normalizeStand)
                .map(s => ({ ...s, eventId: event.id }));

              return relevantStands;
            } catch (error) {
              console.error(`Error fetching stands for event ${event.id}:`, error);
              return [];
            }
          });

          const allStands = await Promise.all(standsPromises);
          if (!isMounted) return;

          // Aggrega risultati
          allStands.forEach((stands, index) => {
            const eventId = eventsList[index].id;
            standsByEvent[eventId] = stands;
            standsCacheRef.current.set(eventId, stands);
          });

          // Update pending stands con sorting
          const allPendingStands = Object.values(standsByEvent).flat().sort((a, b) => {
            const dateA = a.documentsSubmittedAt || a.reservedAt || '';
            const dateB = b.documentsSubmittedAt || b.reservedAt || '';
            return dateB.localeCompare(dateA);
          });

          setPendingStands(allPendingStands);

          if (onStandsChange) {
            onStandsChange(allPendingStands);
          }
        }

        setLastUpdate(new Date());
      } catch (error) {
        if (isMounted) {
          const err = error instanceof Error ? error : new Error(String(error));
          console.error('Error polling events and stands:', err);
          if (onError) {
            onError(err);
          }
        }
      } finally {
        if (isMounted) {
          isPolling = false;
          setIsLoading(false);
        }
      }
    };

    // Prima chiamata immediata
    poll();

    // Polling periodico
    timer = setInterval(poll, interval);

    return () => {
      isMounted = false;
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [enabled, interval, standStatusFilter, onEventsChange, onStandsChange, onError]);

  /**
   * Funzione per forzare refresh manuale
   */
  const refresh = () => {
    eventsHashRef.current = ''; // Force cache invalidation
    standsCacheRef.current.clear();
  };

  /**
   * Funzione per invalidare cache di uno specifico evento
   */
  const invalidateEvent = (eventId: string) => {
    eventsCacheRef.current.delete(eventId);
    standsCacheRef.current.delete(eventId);
    eventsHashRef.current = ''; // Force re-check
  };

  return {
    events,
    pendingStands,
    isLoading,
    lastUpdate,
    refresh,
    invalidateEvent
  };
}

/**
 * Hook semplificato per solo events (senza stands)
 */
export function useEventsOnlyPolling(options: Omit<UseEventsPollingOptions, 'onStandsChange'> = {}) {
  const [events, setEvents] = useState<EventWithStands[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!options.enabled) {
      return;
    }

    let isMounted = true;
    let timer: NodeJS.Timeout | null = null;

    const poll = async () => {
      if (!isMounted) return;

      setIsLoading(true);
      try {
        const response = await fetch('/api/events');
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        if (!isMounted) return;

        const eventsList = Array.isArray(data) ? data : [];
        setEvents(eventsList);

        if (options.onEventsChange) {
          options.onEventsChange(eventsList);
        }
      } catch (error) {
        if (isMounted && options.onError) {
          const err = error instanceof Error ? error : new Error(String(error));
          options.onError(err);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    poll();
    timer = setInterval(poll, options.interval || 20000);

    return () => {
      isMounted = false;
      if (timer) clearInterval(timer);
    };
  }, [options.enabled, options.interval, options.onEventsChange, options.onError]);

  return { events, isLoading };
}
